import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatDateToDDMMYY,
  Checkbox,
  BaseButton,
  Toast,
  useCopyToast,
  Modal,
} from '@repo/common';
import WorkshopDetailOptionList from './WorkshopDetailOptionList';
import { ReactComponent as CopyIcon } from '@repo/common/assets/icons/16px/icon_16px_copy.svg';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as PinIcon } from '@repo/common/assets/icons/16px/icon_16px_pin-left_filled.svg';
import {
  blockUser,
  blockUserAgain,
  cancelBookingByAdmin,
  markAsAbsent,
  markAsAttended,
  markAttendance,
  moveWaitlistDown,
  moveWaitlistUp,
  offerSpot,
  removeFromWaitlist,
  resendOffer,
  sendConfirmationEmail,
  unblockUser,
} from '../../../api/booking/booking.api';
import { assignMvp, removeMvp } from '../../../api/workshop/mvp.api';

const BLOCK_REASONS = [
  {
    value: 'Canceled due to workshop deletion',
    label: 'Canceled due to workshop deletion',
  },
  { value: 'Severe no-show', label: 'Severe no-show' },
  { value: 'Policy violation', label: 'Policy violation' },
  { value: 'Payment issue', label: 'Payment issue' },
  { value: 'Other', label: 'Other' },
];

const ToastCenter = ({ width, children }) => (
  <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
    <div className='w-full' style={{ maxWidth: width }}>
      {children}
    </div>
  </div>
);

const WorkshopDetailRow = ({
  item,
  type,
  checked,
  onSelect,
  onAttend,
  onBlock,
  onUnblock,
  onMvp,
  onCancel,
  onMoveWaitlist,
  onRemoveWaitlist,
  onRefreshWaitlist,
}) => {
  const navigate = useNavigate();
  const defaultProfileImageUrl = `${process.env.REACT_APP_ASSETS_URL}/MyPage/Admin/img-profile-default.svg`;
  const profileImage =
    item?.profileUrl &&
    !item?.profileUrl.startsWith('data:image/') &&
    !item?.profileUrl.startsWith('http://') &&
    !item?.profileUrl.startsWith('https://')
      ? `data:image/jpeg;base64,${item?.profileUrl}`
      : item?.profileUrl || null;
  const [localStatus, setLocalStatus] = useState(item.status);
  const [localBlocked, setLocalBlocked] = useState(item.blocked ?? false);
  const [localIsMvp, setLocalIsMvp] = useState(item.mvp ?? false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [isBlocking, setIsBlocking] = useState(false);

  const { copy, copyToast } = useCopyToast();
  const [showRemoveSuccessToast, setShowRemoveSuccessToast] = useState(false);
  const [showAttendedSuccessToast, setShowAttendedSuccessToast] =
    useState(false);
  const [showAbsentSuccessToast, setShowAbsentSuccessToast] = useState(false);
  const [showEmailSuccessToast, setShowEmailSuccessToast] = useState(false);
  const [showUnblockSuccessToast, setShowUnblockSuccessToast] = useState(false);
  const [showBlockAgainSuccessToast, setShowBlockAgainSuccessToast] =
    useState(false);
  const [showMvpSuccessToast, setShowMvpSuccessToast] = useState(false);
  const [showMvpRemoveSuccessToast, setShowMvpRemoveSuccessToast] =
    useState(false);
  const [showMoveUpWaitlistSuccessToast, setShowMoveUpWaitlistSuccessToast] =
    useState(false);
  const [
    showMoveDownWaitlistSuccessToast,
    setShowMoveDownWaitlistSuccessToast,
  ] = useState(false);
  const [showSendOfferSuccessToast, setShowSendOfferSuccessToast] =
    useState(false);
  const [showOfferSpotSuccessToast, setShowOfferSpotSuccessToast] =
    useState(false);

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [showBlockAgainModal, setShowBlockAgainModal] = useState(false);
  const [showRemoveMvpModal, setShowRemoveMvpModal] = useState(false);
  const [showCancelBookingModal, setShowCancelBookingModal] = useState(false);
  const [showRemoveWaitlistModal, setShowRemoveWaitlistModal] = useState(false);
  const [showOfferSpotModal, setShowOfferSpotModal] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [isCancelBookingLoading, setIsCancelBookingLoading] = useState(false);

  useEffect(() => {
    setLocalStatus(item.status);
  }, [item.status]);

  useEffect(() => {
    setLocalBlocked(item.blocked ?? false);
  }, [item.blocked]);

  const formatStatus = (status) => {
    if (!status || status === 'CONFIRMED') return '-';
    if (status === 'ATTENDED') return 'Completed';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const isNewWaitlist = (waitlistedAt) => {
    if (!waitlistedAt) return false;
    const diff = Date.now() - new Date(waitlistedAt).getTime();
    return diff <= 48 * 60 * 60 * 1000;
  };

  const isPending = item.status === 'OFFERED' || item.status === 'HOLD';

  const handleCopy = () => copy(item.email);

  const handleMarkAsAttended = async () => {
    if (localStatus === 'ATTENDED') {
      setShowRemoveModal(true);
      return;
    }
    try {
      await markAsAttended(item.bookingId);
      setLocalStatus('ATTENDED');
      onAttend(item.bookingId, 'ATTENDED');
      setShowAttendedSuccessToast(false);
      setTimeout(() => setShowAttendedSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to toggle attendance:', err);
    }
  };

  const handleMarkAsAbsent = async () => {
    try {
      await markAsAbsent(item.bookingId);
      setLocalStatus('ABSENT');
      onAttend(item.bookingId, 'ABSENT');
      setShowAbsentSuccessToast(false);
      setTimeout(() => setShowAbsentSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to mark as absent:', err);
    }
  };

  const handleMarkNoShow = async () => {
    try {
      await markAttendance(item.bookingId, 'NO_SHOW');
      setLocalStatus('ABSENT');
      onAttend(item.bookingId, 'ABSENT');
      setShowAbsentSuccessToast(false);
      setTimeout(() => setShowAbsentSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to mark as no-show:', err);
    }
  };

  const handleMarkLate = async () => {
    try {
      await markAttendance(item.bookingId, 'LATE');
      setLocalStatus('ATTENDED');
      onAttend(item.bookingId, 'ATTENDED');
      setShowAttendedSuccessToast(false);
      setTimeout(() => setShowAttendedSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to mark as late:', err);
    }
  };

  const handleContact = () => {
    if (!item.email) return;
    window.open(`mailto:${item.email}`, '_blank');
  };

  const handleOptionClick = (e) => {
    e.stopPropagation();
    if (anchorRect) {
      setAnchorRect(null);
      return;
    }
    setAnchorRect(e.currentTarget.getBoundingClientRect());
  };

  const handleConfirmRemove = async () => {
    try {
      await markAsAttended(item.bookingId);
      setLocalStatus('CONFIRMED');
      onAttend(item.bookingId, 'CONFIRMED');
      setShowRemoveModal(false);
      setShowRemoveSuccessToast(false);
      setTimeout(() => setShowRemoveSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to cancel attendance:', err);
      setShowRemoveModal(false);
    }
  };

  const handleSendConfirmationEmail = async () => {
    try {
      await sendConfirmationEmail(item.bookingId);
      setShowEmailSuccessToast(false);
      setTimeout(() => setShowEmailSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to send confirmation email:', err);
    }
  };

  const handleBlockUser = () => {
    setShowBlockModal(true);
  };

  const handleConfirmBlock = async () => {
    try {
      setIsBlocking(true);
      const blockData = await blockUser(
        item.bookingId,
        blockReason || 'Blocked by admin',
      );
      setIsBlocking(false);
      setShowBlockModal(false);
      setBlockReason('');

      if (localStatus === 'ATTENDED' || localStatus === 'ABSENT') {
        setLocalBlocked(true);
      }

      const blockedEntry = {
        blockId: blockData?.id,
        userId: item.userId,
        userName: item.userName,
        email: item.email,
        profileUrl: item.profileUrl,
        blockedAt: new Date().toISOString(),
        blockStatus: 'ACTIVE',
        blockReason: blockReason || 'Blocked by admin',
      };

      const canceledEntry =
        (type === 'confirmed' && localStatus === 'CONFIRMED') ||
        type === 'waitlisted'
          ? {
              bookingId: item.bookingId,
              bookingSerialNumber: item.bookingSerialNumber,
              userName: item.userName,
              email: item.email,
              profileUrl: item.profileUrl,
              cancelledAt: new Date().toISOString(),
              cancelReason: blockReason || 'Blocked by admin',
              refundPolicyApplied: null,
            }
          : null;

      onBlock?.(item.bookingId, blockedEntry, canceledEntry);
    } catch (err) {
      console.error('Failed to block user:', err);
      setIsBlocking(false);
      setShowBlockModal(false);
    }
  };

  const handleUnblockUser = () => {
    setShowUnblockModal(true);
  };

  const handleConfirmUnblock = async () => {
    try {
      await unblockUser(item.blockId);
      setShowUnblockModal(false);
      setLocalBlocked(false);
      onUnblock?.(item.blockId);
      setShowUnblockSuccessToast(false);
      setTimeout(() => setShowUnblockSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to unblock user:', err);
      setShowUnblockModal(false);
    }
  };

  const handleBlockAgainUser = () => {
    setShowBlockAgainModal(true);
  };

  const handleConfirmBlockAgain = async () => {
    try {
      await blockUserAgain(item.blockId);
      setShowBlockAgainModal(false);
      onUnblock?.(item.blockId, {
        blockStatus: 'ACTIVE',
        blockCount: (item.blockCount ?? 1) + 1,
        blockedAt: new Date().toISOString(),
      });
      setShowBlockAgainSuccessToast(false);
      setTimeout(() => setShowBlockAgainSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to block again:', err);
      setShowBlockAgainModal(false);
    }
  };

  const handleAssignMvp = async () => {
    try {
      await assignMvp(item.bookingId);
      setLocalIsMvp(true);
      onMvp?.(item.bookingId, true);
      setShowMvpSuccessToast(false);
      setTimeout(() => setShowMvpSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to assign MVP:', err);
    }
  };

  const handleRemoveMvp = async () => {
    setShowRemoveMvpModal(true);
  };

  const handleRemoveMvpConfirm = async () => {
    try {
      await removeMvp(item.bookingId);
      setLocalIsMvp(false);
      onMvp?.(item.bookingId, false);
      setShowRemoveMvpModal(false);
      setShowMvpRemoveSuccessToast(false);
      setTimeout(() => setShowMvpRemoveSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to remove MVP:', err);
    }
  };

  const handleCancelBooking = async () => {
    setShowCancelBookingModal(true);
  };

  // TODO: 추후 환불 붙이기
  const handleCancelBookingConfirm = async () => {
    try {
      setIsCancelBookingLoading(true);

      await cancelBookingByAdmin(item.bookingId);
      setShowCancelBookingModal(false);
      onCancel?.(item.bookingId, {
        bookingId: item.bookingId,
        bookingSerialNumber: item.bookingSerialNumber,
        userName: item.userName,
        email: item.email,
        profileUrl: item.profileUrl,
        cancelledAt: new Date().toISOString(),
        cancelReason: 'Canceled by Admin',
        refundPolicyApplied: null,
      });
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      setShowCancelBookingModal(false);
    } finally {
      setIsCancelBookingLoading(false);
    }
  };

  const handleRemoveWaitlist = async () => {
    setShowRemoveWaitlistModal(true);
  };

  const handleRemoveWaitlistConfirm = async () => {
    try {
      await removeFromWaitlist(item.bookingId);
      setShowRemoveWaitlistModal(false);
      onRemoveWaitlist?.(item.bookingId, {
        bookingId: item.bookingId,
        bookingSerialNumber: item.bookingSerialNumber,
        userName: item.userName,
        email: item.email,
        profileUrl: item.profileUrl,
        cancelledAt: new Date().toISOString(),
        cancelReason: 'Removed from waitlist by Admin',
        refundPolicyApplied: null,
      });
    } catch (err) {
      console.error('Failed to remove from waitlist:', err);
      setShowRemoveWaitlistModal(false);
    }
  };

  const handleMoveUpWaitlist = async () => {
    try {
      await moveWaitlistUp(item.bookingId);
      onMoveWaitlist?.('UP', item.bookingId);
      setShowMoveUpWaitlistSuccessToast(false);
      setTimeout(() => setShowMoveUpWaitlistSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to move up waitlist:', err);
    }
  };

  const handleMoveDownWaitlist = async () => {
    try {
      await moveWaitlistDown(item.bookingId);
      onMoveWaitlist?.('DOWN', item.bookingId);
      setShowMoveDownWaitlistSuccessToast(false);
      setTimeout(() => setShowMoveDownWaitlistSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to move down waitlist:', err);
    }
  };

  const handleSendOffer = async () => {
    try {
      await resendOffer(item.bookingId);
      setShowSendOfferSuccessToast(false);
      setTimeout(() => setShowSendOfferSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to resend offer mail:', err);
    }
  };

  const handleOfferSpot = async () => {
    setShowOfferSpotModal(true);
  };

  const handleOfferSpotConfirm = async () => {
    try {
      await offerSpot(item.bookingId);
      setShowOfferSpotModal(false);
      setShowOfferSpotSuccessToast(false);
      setTimeout(() => setShowOfferSpotSuccessToast(true), 10);
      onRefreshWaitlist?.();
    } catch (err) {
      console.error('Failed to offer spot:', err);
      setShowOfferSpotModal(false);
    }
  };

  const handleViewInvoice = () => {
    navigate(`/mypage?tab=Payments&invoice=${item.invoiceNumber}`);
  };

  return (
    <div className='flex items-center w-full bg-grayscale-gray00 rounded-lg typo-b14 text-text-secondary min-h-[52px] h-full py-2'>
      <div
        className='flex px-2 items-center'
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={checked}
          onChange={(e) =>
            onSelect(
              type === 'blocked' ? item.blockId : item.bookingId,
              e.target.checked,
            )
          }
        />
      </div>

      <div className='flex px-2 items-center'>
        {type === 'waitlisted' && (
          <span className='flex justify-center items-center gap-0.5 w-6 h-6 px-1 py-0.5 text-text-primary typo-b12Strong'>
            {isPending ? '-' : item.waitlistPosition}
          </span>
        )}
        {profileImage ? (
          <img
            src={profileImage}
            alt='User profile'
            className='w-6 h-6 object-cover rounded-full'
            loading='lazy'
          />
        ) : (
          <img
            src={defaultProfileImageUrl}
            alt='User profile'
            className='w-6 h-6 object-cover rounded-full'
            loading='lazy'
          />
        )}
      </div>
      <span
        className={`text-text-primary flex-1 ${
          type === 'confirmed' || type === 'canceled' || type === 'blocked'
            ? 'min-w-[200px] max-w-[380px]'
            : 'min-w-[200px]'
        } w-full px-2 flex items-center justify-start typo-b16Strong truncate gap-2`}
      >
        {type === 'waitlisted' && isPending && (
          <BaseButton
            icon={<PinIcon />}
            type='text'
            size='s'
            bgclass='bg-purple-purple01 text-purple-purple05 px-2 py-0.5 h-[24px] typo-b12Strong'
            gap='gap-0.5'
            width='w-[81px]'
            isStatic
          >
            Pending
          </BaseButton>
        )}

        {type === 'waitlisted' && isNewWaitlist(item.waitlistedAt) && (
          <BaseButton
            hideIcon
            type='text'
            size='s'
            bgclass='border-grayscale-gray05 bg-grayscale-gray00 px-2 py-0.5 h-[24px] typo-b12Strong'
            width='w-[41px]'
            isStatic
          >
            New
          </BaseButton>
        )}

        {localBlocked && (
          <BaseButton
            hideIcon
            type='text'
            size='s'
            width='w-[63px]'
            bgclass='bg-grayscale-gray02 text-grayscale-gray05 px-2 py-0.5 typo-b12Strong h-6'
            isStatic
          >
            Blocked
          </BaseButton>
        )}
        {localIsMvp && (
          <BaseButton
            icon={
              <span className='text-icon-inverse'>
                <PinIcon />
              </span>
            }
            type='text'
            size='s'
            width='w-[62px]'
            bgclass='bg-orange-orange04 text-orange-orange01 px-2 py-0.5 typo-b12Strong h-6'
            textclass='text-icon-inverse'
            isStatic
            gap='gap-0.5'
          >
            MVP
          </BaseButton>
        )}
        {item.userName}
      </span>
      <span
        className={`${type === 'confirmed' && 'flex-1'} ${
          type === 'canceled' || type === 'blocked'
            ? 'max-w-[260px]'
            : 'max-w-[380px]'
        } min-w-[200px] w-full px-2 flex items-center justify-center gap-1`}
      >
        {item.email}
        <button
          onClick={handleCopy}
          className='text-icon-tertiary hover:text-icon-placeholder'
        >
          <CopyIcon />
        </button>
        {/* 예약 전 사전 질문에 답한 사람만 표시 — 알러지 등 준비에 필요한 정보 */}
        {item.answers?.length > 0 && (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              setShowAnswersModal(true);
            }}
            title='View pre-booking answers'
            className='shrink-0 rounded-full border border-grayscale-gray03 px-2 py-0.5 typo-b12Strong text-text-secondary hover:border-green-deep02 hover:text-green-deep03'
          >
            Answers {item.answers.length}
          </button>
        )}
      </span>

      {type === 'confirmed' && (
        <>
          <span className='min-w-[120px] max-w-[240px] px-2 flex justify-center items-center w-full'>
            {item.confirmedAt ? formatDateToDDMMYY(item.confirmedAt) : '-'}
          </span>
          <div className='min-w-[120px] max-w-[240px] px-2 flex justify-center items-center w-full'>
            <BaseButton
              hideIcon
              type='text'
              size='s'
              bgclass={`px-2 py-0.5 h-6 ${
                localStatus === 'ATTENDED'
                  ? 'text-green-deep04 bg-green-light02'
                  : localStatus === 'ABSENT'
                    ? 'text-grayscale-gray00 bg-grayscale-gray05'
                    : 'bg-transparent border-none'
              }`}
              isStatic
            >
              {formatStatus(localStatus)}
            </BaseButton>
          </div>
          <div className='w-[160px] px-2 flex justify-center items-center'>
            <BaseButton
              hideIcon
              type='text_basic'
              size='s'
              bgclass={`py-1.5 ${
                showRemoveModal ? 'bg-grayscale-gray02 text-text-primary' : ''
              }`}
              onClick={handleMarkAsAttended}
            >
              Mark as Attended
            </BaseButton>
          </div>
        </>
      )}

      {type === 'waitlisted' && (
        <>
          <span className='flex min-w-[120px] max-w-[260px] px-2 justify-center items-center gap-1 text-text-secondary w-full'>
            {item.waitlistedAt ? formatDateToDDMMYY(item.waitlistedAt) : '-'}
          </span>
        </>
      )}

      {type === 'canceled' && (
        <>
          <span className='flex min-w-[120px] max-w-[180px] px-2 justify-center items-center gap-1 text-text-secondary w-full'>
            {item.cancelledAt ? formatDateToDDMMYY(item.cancelledAt) : '-'}
          </span>
          <span className='flex min-w-[120px] max-w-[220px] px-2 justify-center items-center gap-1 text-text-secondary w-full text-center'>
            {item.cancelReason ?? '-'}
          </span>
          <span className='flex min-w-[120px] max-w-[220px] px-2 justify-center items-center gap-1 text-text-secondary w-full'>
            {item.refundStatus ?? '-'}
          </span>
        </>
      )}

      {type === 'blocked' && (
        <>
          <span className='flex min-w-[120px] max-w-[120px] px-2 justify-center items-center gap-1 text-text-secondary w-full'>
            {item.blockedAt ? formatDateToDDMMYY(item.blockedAt) : '-'}
          </span>
          <span className='flex min-w-[120px] max-w-[120px] px-2 justify-center items-center gap-1 text-text-secondary w-full'>
            {item.unblockedAt ? formatDateToDDMMYY(item.unblockedAt) : '-'}
          </span>
          <span className='flex min-w-[120px] max-w-[200px] px-2 justify-center items-center gap-1 text-text-secondary w-full text-center'>
            {item.blockReason ?? '-'}
          </span>
          <span className='flex flex-col min-w-[120px] max-w-[120px] px-2 justify-center items-center text-text-secondary w-full'>
            <span>
              {item.blockStatus === 'ACTIVE' ? 'Blocked' : 'Block Expired'}
            </span>
            {item.blockCount >= 2 && item.blockStatus === 'ACTIVE' && (
              <span className='typo-b12 text-text-tertiary'>
                (
                {item.blockCount === 2
                  ? '2nd'
                  : item.blockCount === 3
                    ? '3rd'
                    : `${item.blockCount}th`}{' '}
                time)
              </span>
            )}
          </span>
        </>
      )}

      {(type === 'waitlisted' || type === 'canceled' || type === 'blocked') && (
        <div
          className={`${
            type === 'waitlisted' ? 'w-[180px]' : 'w-[120px]'
          } px-2 flex justify-center items-center`}
        >
          <BaseButton
            hideIcon
            type='text_basic'
            size='s'
            bgclass='py-1.5'
            onClick={handleContact}
          >
            Contact
          </BaseButton>
        </div>
      )}

      <div className='w-[40px] px-0.5 flex justify-center items-center gap-2.5'>
        <button
          className='text-icon-placeholder hover:text-icon-tertiary'
          onClick={handleOptionClick}
        >
          <OptionIcon className='w-5 h-5' />
        </button>
      </div>

      <WorkshopDetailOptionList
        anchorRect={anchorRect}
        onClose={() => setAnchorRect(null)}
        type={type}
        status={localStatus}
        blockStatus={item.blockStatus}
        onMarkAttended={handleMarkAsAttended}
        onMarkAbsent={handleMarkAsAbsent}
        onMarkNoShow={handleMarkNoShow}
        onMarkLate={handleMarkLate}
        onContact={handleContact}
        onConfirmationEmail={handleSendConfirmationEmail}
        onBlockUser={handleBlockUser}
        onUnblockUser={handleUnblockUser}
        onBlockAgainUser={handleBlockAgainUser}
        onAssignMvp={handleAssignMvp}
        onRemoveMvp={handleRemoveMvp}
        onCancelBooking={handleCancelBooking}
        onRemoveWaitlist={handleRemoveWaitlist}
        onMoveUpWaitlist={handleMoveUpWaitlist}
        onMoveDownWaitlist={handleMoveDownWaitlist}
        onSendOffer={handleSendOffer}
        onOfferSpot={handleOfferSpot}
        onViewInvoice={handleViewInvoice}
        isBlocked={item.blocked}
        isMvp={localIsMvp}
        isPending={isPending}
      />

      {copyToast}

      {showRemoveSuccessToast && (
        <ToastCenter width='254px'>
          <Toast
            title='Marked as unmarked.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showAttendedSuccessToast && (
        <ToastCenter width='242px'>
          <Toast
            title='Marked as attended.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showAbsentSuccessToast && (
        <ToastCenter width='228px'>
          <Toast
            title='Marked as absent.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showEmailSuccessToast && (
        <ToastCenter width='274px'>
          <Toast
            title='Confirmation email sent.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showUnblockSuccessToast && (
        <ToastCenter width='208px'>
          <Toast
            title='User unblocked.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showBlockAgainSuccessToast && (
        <ToastCenter width='278px'>
          <Toast
            title='User blocked for 30 days.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showMvpSuccessToast && (
        <ToastCenter width='199px'>
          <Toast
            title='MVP assigned.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showMvpRemoveSuccessToast && (
        <ToastCenter width='193px'>
          <Toast
            title='MVP removed.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showMoveUpWaitlistSuccessToast && (
        <ToastCenter width='239px'>
          <Toast
            title='Moved up in waitlist.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showMoveDownWaitlistSuccessToast && (
        <ToastCenter width='260px'>
          <Toast
            title='Moved down in waitlist.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showSendOfferSuccessToast && (
        <ToastCenter width='278px'>
          <Toast
            title='Confirmation email sent.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showOfferSpotSuccessToast && (
        <ToastCenter width='368px'>
          <Toast
            title='Spot offered. User moved to pending.'
            hierarchy='important'
            direction='up'
            customWidth='100%'
          />
        </ToastCenter>
      )}

      {showAnswersModal && (
        /* 공용 Modal 은 본문 슬롯이 없어 답변 목록을 직접 그린다 */
        <>
          <div
            className='fixed inset-0 z-[9998] bg-surface-black40'
            onClick={() => setShowAnswersModal(false)}
          />
          <div className='fixed inset-0 z-[9999] flex items-center justify-center px-4'>
            <div
              className='flex max-h-[80vh] w-full max-w-[520px] flex-col rounded-[24px] bg-grayscale-gray00 p-6 shadow-2'
              onClick={(e) => e.stopPropagation()}
            >
              <p className='typo-b18Strong text-text-primary'>
                Pre-booking answers
              </p>
              <p className='mt-1 typo-b13 text-text-placeholder'>
                {item.userName} · {item.email}
              </p>

              <div className='mt-5 flex flex-1 flex-col gap-4 overflow-y-auto'>
                {item.answers.map((answer) => (
                  <div key={answer.questionId}>
                    <p className='typo-b13 text-text-placeholder'>
                      {answer.question}
                    </p>
                    <p className='mt-1 whitespace-pre-wrap typo-b14 text-text-primary'>
                      {answer.answer}
                    </p>
                  </div>
                ))}
              </div>

              <div className='mt-6 flex justify-end'>
                <BaseButton
                  type='primary'
                  size='m'
                  hideIcon
                  width='120px'
                  onClick={() => setShowAnswersModal(false)}
                >
                  Close
                </BaseButton>
              </div>
            </div>
          </div>
        </>
      )}

      {showRemoveModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => setShowRemoveModal(false)}
          onClickRightBtn={handleConfirmRemove}
          title='Remove attendance?'
          subText='This will remove the attendance status and
set the participant back to unmarked.'
          buttonSize='xl'
          leftButtonLabel='Keep as Attended'
          leftHideIcon={true}
          rightButtonLabel='Remove'
          rightHideIcon={true}
          type='warning'
        />
      )}

      {showBlockModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowBlockModal(false);
            setBlockReason('');
          }}
          onClickRightBtn={handleConfirmBlock}
          title={'Block user from this workshop?'}
          subText={`${
            type === 'confirmed'
              ? 'This will block the user from this workshop for 30 days. Any upcoming bookings will be canceled. This action cannot be undone.'
              : type === 'waitlisted'
                ? 'This will block the user from booking this workshop for 30 days. The user will be removed from the waitlist and cannot join upcoming sessions. This action cannot be undone.'
                : `This will block the user from 
              booking this workshop for 30 days. 
              This action cannot be undone.`
          }`}
          buttonSize='xl'
          leftButtonLabel='cancel'
          leftHideIcon={true}
          rightButtonLabel='Block user'
          rightHideIcon={true}
          type='criticalError'
          showDropDown
          dropDownValue={blockReason}
          onDropDownChange={setBlockReason}
          dropDownOptions={BLOCK_REASONS}
          rightButtonLoading={isBlocking}
        />
      )}

      {showUnblockModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowUnblockModal(false);
          }}
          onClickRightBtn={handleConfirmUnblock}
          title={'Unblock user now?'}
          subText={`This will restore the user’s access to this workshop immediately. They will be able to book upcoming sessions again.`}
          buttonSize='xl'
          leftButtonLabel='cancel'
          leftHideIcon={true}
          rightButtonLabel='Unblock now'
          rightHideIcon={true}
        />
      )}

      {showBlockAgainModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowBlockAgainModal(false);
          }}
          onClickRightBtn={handleConfirmBlockAgain}
          title={'Block user again?'}
          subText={`This will block the user from booking this workshop for another 30 days. This action cannot be undone.`}
          buttonSize='xl'
          leftButtonLabel='cancel'
          leftHideIcon={true}
          rightButtonLabel='Block again'
          rightHideIcon={true}
          type='warning'
        />
      )}

      {showRemoveMvpModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowRemoveMvpModal(false);
          }}
          onClickRightBtn={handleRemoveMvpConfirm}
          title={'Remove MVP?'}
          subText={`This action will remove MVP status
and may affect rewards.`}
          buttonSize='xl'
          leftButtonLabel='cancel'
          leftHideIcon={true}
          rightButtonLabel='Remove'
          rightHideIcon={true}
          type='warning'
        />
      )}

      {showCancelBookingModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowCancelBookingModal(false);
          }}
          onClickRightBtn={handleCancelBookingConfirm}
          title={'Cancel booking?'}
          subText={`This will cancel the participant’s booking.
This action cannot be undone.`}
          buttonSize='xl'
          leftButtonLabel='Keep'
          leftHideIcon={true}
          rightButtonLabel='Cancel'
          rightHideIcon={true}
          type='warning'
          rightButtonLoading={isCancelBookingLoading}
        />
      )}

      {showRemoveWaitlistModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowRemoveWaitlistModal(false);
          }}
          onClickRightBtn={handleRemoveWaitlistConfirm}
          title={'Remove from waitlist?'}
          subText={`This will remove the user from the waitlist
for this workshop.`}
          buttonSize='xl'
          leftButtonLabel='cancel'
          leftHideIcon={true}
          rightButtonLabel='Remove'
          rightHideIcon={true}
          type='warning'
        />
      )}

      {showOfferSpotModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowOfferSpotModal(false);
          }}
          onClickRightBtn={handleOfferSpotConfirm}
          title={'Offer spot to this user?'}
          subText={`This will move the user to pending and reserve
a spot for this workshop. A temporary seat will be created if no spots are available. This action cannot be undone.`}
          buttonSize='xl'
          leftButtonLabel='cancel'
          leftHideIcon={true}
          rightButtonLabel='Offer spot'
          rightHideIcon={true}
          type='noticeError'
        />
      )}
    </div>
  );
};

export default WorkshopDetailRow;
